'use client';
import { Icon } from '@/src/components/ui';
import { Button } from '@workspace/ui/components/button';
import { useRouter } from 'next/navigation';

type Props = {
  title?: string;
  description?: string;
};

type Pixel = {
  className: string;
  rot: string;
  floatDur: string;
  twinkleDur: string;
  floatDelay: string;
  twinkleDelay: string;
  opacity?: number;
};

const pixels: Pixel[] = [
  {
    className: '-left-10 -top-7 h-3 w-3 bg-blue-primary',
    rot: 'rotate(12deg)',
    floatDur: '4.2s',
    twinkleDur: '2.2s',
    floatDelay: '0.1s',
    twinkleDelay: '0.3s',
  },
  {
    className: '-left-6 top-10 h-2.5 w-2.5 bg-pink-accent',
    rot: 'rotate(-18deg)',
    floatDur: '3.6s',
    twinkleDur: '2.9s',
    floatDelay: '0.35s',
    twinkleDelay: '0.1s',
  },
  {
    className: '-left-2 -top-12 h-2 w-2 bg-blue-primary',
    rot: 'rotate(30deg)',
    floatDur: '4.8s',
    twinkleDur: '3.1s',
    floatDelay: '0.2s',
    twinkleDelay: '0.6s',
    opacity: 0.7,
  },

  {
    className: '-right-12 top-3 h-3.5 w-3.5 bg-pink-accent',
    rot: 'rotate(-8deg)',
    floatDur: '3.9s',
    twinkleDur: '2.4s',
    floatDelay: '0.5s',
    twinkleDelay: '0.2s',
  },
  {
    className: '-right-6 top-14 h-2 w-2 bg-blue-primary',
    rot: 'rotate(22deg)',
    floatDur: '4.6s',
    twinkleDur: '3s',
    floatDelay: '0.15s',
    twinkleDelay: '0.4s',
  },
  {
    className: '-right-3 -bottom-10 h-2.5 w-2.5 bg-pink-accent',
    rot: 'rotate(-26deg)',
    floatDur: '3.4s',
    twinkleDur: '2.1s',
    floatDelay: '0.6s',
    twinkleDelay: '0.55s',
    opacity: 0.75,
  },

  {
    className: 'left-16 -top-14 h-1.5 w-1.5 bg-blue-primary',
    rot: 'rotate(10deg)',
    floatDur: '4.9s',
    twinkleDur: '3.2s',
    floatDelay: '0.25s',
    twinkleDelay: '0.15s',
    opacity: 0.6,
  },
  {
    className: 'right-10 -top-9 h-2 w-2 bg-pink-accent',
    rot: 'rotate(44deg)',
    floatDur: '3.7s',
    twinkleDur: '2.5s',
    floatDelay: '0.45s',
    twinkleDelay: '0.35s',
    opacity: 0.6,
  },
  {
    className: 'left-24 top-20 h-1.5 w-1.5 bg-blue-primary',
    rot: 'rotate(-12deg)',
    floatDur: '4.1s',
    twinkleDur: '2.8s',
    floatDelay: '0.12s',
    twinkleDelay: '0.5s',
    opacity: 0.6,
  },

  {
    className: '-left-16 top-20 h-1.5 w-1.5 bg-pink-accent',
    rot: 'rotate(6deg)',
    floatDur: '5.2s',
    twinkleDur: '3.4s',
    floatDelay: '0.33s',
    twinkleDelay: '0.25s',
    opacity: 0.5,
  },
  {
    className: 'right-20 -bottom-12 h-2 w-2 bg-blue-primary',
    rot: 'rotate(-18deg)',
    floatDur: '3.8s',
    twinkleDur: '2.6s',
    floatDelay: '0.22s',
    twinkleDelay: '0.42s',
    opacity: 0.55,
  },
  {
    className: '-right-16 -bottom-2 h-1.5 w-1.5 bg-pink-accent',
    rot: 'rotate(18deg)',
    floatDur: '4.4s',
    twinkleDur: '2.3s',
    floatDelay: '0.58s',
    twinkleDelay: '0.18s',
    opacity: 0.55,
  },
];

export function DeletedAccountPageContent({
  title = 'This account was deleted',
  description = 'The profile you’re trying to view is no longer available.',
}: Props) {
  const router = useRouter();

  return (
    <div className="relative min-h-[calc(100vh-500px)] overflow-hidden w-full">
      <div className="relative mx-auto w-full max-w-[980px] px-4 md:px-8 py-4 md:py-14">
        <Button
          variant="ghost"
          onClick={() => router.back()}
          className="px-4 py-3.5 rounded-[8px] border-0 md:text-lg text-base h-10 group hover:bg-transparent hover:text-blue-primary transition duration-300 ease-in-out cursor-pointer"
        >
          <Icon
            name="back"
            className="md:size-5 size-4 group-hover"
          />
          Back
        </Button>

        <div className="flex flex-col items-center text-center mt-12 md:mt-16">
          <div className="relative mt-10 md:mt-12">
            <div className="h-20 w-20 md:h-24 md:w-24 flex items-center justify-center relative z-10 ">
              <Icon name="deleted-account" className="size-16 md:size-16" />
            </div>

            {pixels.map((p, idx) => (
              <span
                key={idx}
                className={`p3-pixel ${p.className}`}
                style={{
                  ['--p3-pixel-rot' as any]: p.rot,
                  ['--p3-pixel-float-dur' as any]: p.floatDur,
                  ['--p3-pixel-twinkle-dur' as any]: p.twinkleDur,
                  ['--p3-pixel-float-delay' as any]: p.floatDelay,
                  ['--p3-pixel-twinkle-delay' as any]: p.twinkleDelay,
                  opacity: p.opacity,
                }}
              />
            ))}
          </div>

          <h1 className="mt-10 md:mt-12 text-3xl! md:text-5xl! font-semibold! tracking-tight!">
            {title}
          </h1>

          <p className="mt-4 text-base! md:text-lg! text-black/65 max-w-[720px] leading-relaxed!">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}
